import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaHotel, 
  FaCalendarAlt, 
  FaUsers, 
  FaArrowRight,
  FaCheck,
  FaStar,
  FaMapMarkerAlt,
  FaBed,
  FaWifi,
  FaSwimmingPool,
  FaCar,
  FaConciergeBell,
  FaUtensils
} from 'react-icons/fa';

const HotelBooking = () => {
  const [formData, setFormData] = useState({
    destination: '',
    checkInDate: '',
    checkOutDate: '',
    guests: 1,
    rooms: 1,
    starRating: '',
    budgetRange: '',
    amenities: [],
    specialRequests: '',
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const nigerianDestinations = [
    'Lagos - Victoria Island',
    'Lagos - Ikeja',
    'Lagos - Lekki',
    'Abuja - Central Area',
    'Abuja - Garki',
    'Abuja - Wuse',
    'Kano - City Center',
    'Port Harcourt - GRA',
    'Calabar - City Center',
    'Enugu - Independence Layout',
    'Jos - City Center',
    'Ibadan - Ring Road',
    'Benin City - City Center',
    'Warri - City Center',
    'Owerri - City Center'
  ];

  const starRatings = [
    { value: '3', label: '3 Star', description: 'Comfortable & Budget-friendly' },
    { value: '4', label: '4 Star', description: 'Superior Quality & Service' },
    { value: '5', label: '5 Star', description: 'Luxury & Premium Experience' },
    { value: 'any', label: 'Any Rating', description: 'Show me all options' }
  ];

  const amenityOptions = [
    { value: 'wifi', label: 'Free WiFi', icon: FaWifi },
    { value: 'pool', label: 'Swimming Pool', icon: FaSwimmingPool },
    { value: 'parking', label: 'Free Parking', icon: FaCar },
    { value: 'breakfast', label: 'Breakfast Included', icon: FaUtensils },
    { value: 'gym', label: 'Fitness Center', icon: FaConciergeBell },
    { value: 'spa', label: 'Spa & Wellness', icon: FaConciergeBell },
    { value: 'restaurant', label: 'Restaurant', icon: FaUtensils },
    { value: 'room-service', label: '24/7 Room Service', icon: FaConciergeBell }
  ];

  const budgetRanges = [
    { value: 'budget', label: '30,000 - 60,000', description: 'Budget-friendly options' },
    { value: 'mid-range', label: '60,000 - 150,000', description: 'Mid-range comfort' },
    { value: 'luxury', label: '150,000 - 300,000', description: 'Luxury experience' },
    { value: 'premium', label: '300,000+', description: 'Ultra-premium & suites' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAmenityChange = (amenity) => {
    const updatedAmenities = formData.amenities.includes(amenity)
      ? formData.amenities.filter(a => a !== amenity)
      : [...formData.amenities, amenity];
    
    setFormData({
      ...formData,
      amenities: updatedAmenities
    });
  };

  const handleCountChange = (field, increment) => {
    const newValue = Math.max(1, formData[field] + (increment ? 1 : -1));
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formDataToSend = {
      ...formData,
      _captcha: false,
      _template: "table",
      _autoresponse: "Thanks for your request! We'll respond shortly.",
      _token: "10d0b5f657f51e47f0b98f7f43832928", // Replace with your actual token
    };
  
    const encoded = new URLSearchParams(formDataToSend).toString();
  
    try {
      await fetch("https://formsubmit.co/10d0b5f657f51e47f0b98f7f43832928", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });
  
      // Show success message in UI
      setIsSubmitted(true);
  
      // Optionally clear the form
      // setFormData(initialState);
  
      // Hide after a few seconds if needed
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("FormSubmit failed:", error);
      alert("There was a problem submitting the form.");
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-green overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/14210678/pexels-photo-14210678.jpeg')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaHotel className="text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold">Hotel Booking</h1>
            </div>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Discover comfortable accommodations across Nigeria. From budget-friendly stays 
              to luxury resorts, find the perfect hotel for your business or leisure trip.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <FaCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Hotel Request Submitted!</h3>
              <p className="text-lg text-gray-600 mb-4">
                Thank you for choosing Yowa Travels for your hotel booking.
              </p>
              <p className="text-green-600 font-semibold text-lg">
                An agent will be with you shortly to provide the best hotel options for your stay!
              </p>
              <div className="mt-8 p-6 bg-green-50 rounded-2xl">
                <p className="text-sm text-green-700">
                  <strong>What's Next?</strong><br />
                  We'll send you curated hotel recommendations with photos, amenities, and special rates within an hour.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                {/* Destination Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-green-500" />
                    <span>Destination</span>
                  </h4>
                  
                  <div>
                    <label className="form-label">Where would you like to stay? *</label>
                    <select
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="">Select destination</option>
                      {nigerianDestinations.map((destination, index) => (
                        <option key={index} value={destination}>
                          {destination}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Check-in/Check-out Dates */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaCalendarAlt className="text-green-500" />
                    <span>Stay Dates</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Check-in Date *</label>
                      <input
                        type="date"
                        name="checkInDate"
                        value={formData.checkInDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Check-out Date *</label>
                      <input
                        type="date"
                        name="checkOutDate"
                        value={formData.checkOutDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={formData.checkInDate || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Guests and Rooms */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaUsers className="text-green-500" />
                    <span>Guests & Rooms</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="font-bold text-gray-800">Guests</h5>
                          <p className="text-sm text-gray-600">Total number of guests</p>
                        </div>
                        <FaUsers className="text-green-500 text-xl" />
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleCountChange('guests', false)}
                          disabled={formData.guests <= 1}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-xl font-bold text-gray-800 mx-4">
                          {formData.guests}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCountChange('guests', true)}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="font-bold text-gray-800">Rooms</h5>
                          <p className="text-sm text-gray-600">Number of rooms needed</p>
                        </div>
                        <FaBed className="text-green-500 text-xl" />
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleCountChange('rooms', false)}
                          disabled={formData.rooms <= 1}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-xl font-bold text-gray-800 mx-4">
                          {formData.rooms}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCountChange('rooms', true)}
                          className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-green-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Star Rating Preference */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaStar className="text-green-500" />
                    <span>Preferred Star Rating</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {starRatings.map((rating) => (
                      <label
                        key={rating.value}
                        className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                          formData.starRating === rating.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="starRating"
                          value={rating.value}
                          checked={formData.starRating === rating.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="flex justify-center mb-2">
                            {rating.value !== 'any' ? (
                              [...Array(parseInt(rating.value))].map((_, i) => (
                                <FaStar key={i} className="text-yellow-400 text-lg" />
                              ))
                            ) : (
                              <FaStar className="text-gray-400 text-lg" />
                            )}
                          </div>
                          <h5 className="font-bold text-gray-800 mb-2">{rating.label}</h5>
                          <p className="text-sm text-gray-600">{rating.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Budget Range */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Budget Range (per night)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {budgetRanges.map((budget) => (
                      <label
                        key={budget.value}
                        className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                          formData.budgetRange === budget.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="budgetRange"
                          value={budget.value}
                          checked={formData.budgetRange === budget.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <h5 className="font-bold text-gray-800 mb-2">{budget.label}</h5>
                          <p className="text-sm text-gray-600">{budget.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Preferred Amenities</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {amenityOptions.map((amenity) => {
                      const IconComponent = amenity.icon;
                      return (
                        <label
                          key={amenity.value}
                          className={`cursor-pointer border-2 rounded-xl p-4 transition-all duration-300 ${
                            formData.amenities.includes(amenity.value)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.amenities.includes(amenity.value)}
                            onChange={() => handleAmenityChange(amenity.value)}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <IconComponent className="text-green-500 text-2xl mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-800">{amenity.label}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Special Requests */}
                <div className="mb-8">
                  <label className="form-label">Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="Room preferences, accessibility needs, celebration arrangements, or any other special requirements..."
                  ></textarea>
                </div>

                {/* Contact Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Whatsapp Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+234 (0) 900 000 0000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-yowa-primary text-lg px-12 py-4 inline-flex items-center space-x-3"
                  >
                    <FaHotel />
                    <span>Find Hotels</span>
                    <FaArrowRight />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    No payment required. We'll send you the best hotel options with real-time availability.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Hotel Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Book Hotels with Yowa Travels?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enjoy exclusive benefits, verified accommodations, and personalized service for every hotel booking.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaCheck,
                title: 'Verified Hotels Only',
                description: 'All hotels are personally inspected and verified for quality, safety, and service standards.',
                color: 'bg-green-500'
              },
              {
                icon: FaStar,
                title: 'Best Rate Guarantee',
                description: 'Found a lower rate elsewhere? We\'ll match it and give you an additional 5% off.',
                color: 'bg-yellow-500'
              },
              {
                icon: FaConciergeBell,
                title: 'Concierge Support',
                description: 'Personal assistance with room upgrades, special requests, and local recommendations.',
                color: 'bg-purple-500'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HotelBooking;